'use client';

import { apiFetch } from '@/lib/utils/api';
import { Stack } from '@slims/ui/atoms';
import {
	AuthField,
	AuthHeader,
	AuthInlineEmailForm,
	AuthNote,
	AuthNotice,
	AuthScreen,
	AuthSubmit,
	AuthTextButton,
} from '@slims/ui/organisms';
import { useRouter, useSearchParams } from '@/lib/next/navigation';
import { Suspense, useState } from 'react';
import { createSupabaseBrowser } from '@/lib/supabase/client';
import { passwordRecoveryRedirect } from '@/lib/auth-recovery';
import { useUIStore } from '@/stores/ui-store';

function SignInForm() {
	const router = useRouter();
	const theme = useUIStore((s) => s.theme);
	const searchParams = useSearchParams();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const isInvited = searchParams.get('invited') === '1';
	const [error, setError] = useState(
		searchParams.get('error')
			? "Errore durante il login. Riprova o contatta l'amministratore."
			: '',
	);
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);
		setError('');

		const supabase = createSupabaseBrowser();
		const { error: authError } = await supabase.auth.signInWithPassword({
			email,
			password,
		});

		if (authError) {
			setError(authError.message);
			setLoading(false);
			return;
		}

		// Fetch user context to determine redirect: a superadmin on a fresh
		// instance (no orgs yet) lands on the CER management area.
		const res = await apiFetch('/api/auth/me');
		if (res.ok) {
			const data = await res.json();
			if (data.role === 'member') {
				router.push('/myslims');
				return;
			}
			const orgs = (data.availableOrgs ?? []) as unknown[];
			if (data.role === 'superadmin' && orgs.length === 0) {
				router.push('/cer');
				return;
			}
		}
		router.push('/dashboard');
	};

	return (
		<Stack gap="xl">
			<AuthHeader
				theme={theme}
				title={isInvited ? 'Benvenuto nella CER' : 'Accedi alla piattaforma'}
				subtitle={
					isInvited
						? 'Il tuo account è stato attivato. Inserisci la tua email e imposta una password per accedere.'
						: 'Inserisci le credenziali per continuare'
				}
			/>

			<form onSubmit={handleSubmit}>
				<Stack gap="lg">
					{error && <AuthNotice>{error}</AuthNotice>}

					<AuthField
						id="email"
						label="Email"
						type="email"
						value={email}
						onChange={setEmail}
						required
						placeholder="nome@esempio.it"
					/>

					<AuthField
						id="password"
						label="Password"
						type="password"
						value={password}
						onChange={setPassword}
						required
						placeholder="••••••••"
					/>

					<AuthSubmit disabled={loading}>{loading ? 'Accesso in corso...' : 'Accedi'}</AuthSubmit>
				</Stack>
			</form>

			{isInvited ? (
				<PasswordResetForm hint="Prima volta? Imposta la tua password:" />
			) : (
				<Stack gap="md">
					<AuthNote>Contatta il tuo amministratore per ottenere le credenziali.</AuthNote>
					<PasswordResetDisclosure />
				</Stack>
			)}
		</Stack>
	);
}

export default function SignInPage() {
	return (
		<AuthScreen>
			<Suspense>
				<SignInForm />
			</Suspense>
		</AuthScreen>
	);
}

/**
 * Chi arriva senza invito vede prima un link e poi il form: il campo email in
 * chiaro sotto quello del login confonde la pagina, ma il recupero deve
 * comunque essere raggiungibile senza passare dall'amministratore (INB-6).
 */
function PasswordResetDisclosure() {
	const [open, setOpen] = useState(false);

	if (!open) {
		return <AuthTextButton onClick={() => setOpen(true)}>Password dimenticata?</AuthTextButton>;
	}
	return <PasswordResetForm hint="Ti invieremo un'email per reimpostare la password:" />;
}

function PasswordResetForm({ hint }: { hint: string }) {
	const [email, setEmail] = useState('');
	const [sent, setSent] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	async function handleReset(e: React.FormEvent) {
		e.preventDefault();
		setLoading(true);
		setError('');
		const supabase = createSupabaseBrowser();
		// Lo stesso percorso degli inviti, ammesso da Supabase: un `redirectTo`
		// non ammesso ricade sulla dashboard, già loggati, senza mai far
		// scegliere la password (INB-362, vedi `lib/auth-recovery`).
		const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
			redirectTo: passwordRecoveryRedirect(window.location.origin),
		});
		setLoading(false);

		// Supabase risponde ok anche per email inesistenti (niente enumerazione
		// degli account): gli unici errori qui sono rate limit o invio fallito.
		if (resetError) {
			setError(
				resetError.status === 429
					? 'Troppe richieste. Riprova tra qualche minuto.'
					: "Invio non riuscito. Riprova o contatta l'amministratore.",
			);
			return;
		}
		setSent(true);
	}

	if (sent) {
		return (
			// Supabase risponde ok anche per un indirizzo senza account: «Email
			// inviata!» faceva cercare un'email mai partita.
			<AuthNote tone="success">
				Se l'indirizzo {email} è registrato, riceverai un'email per impostare la password.
				Controlla anche lo spam; se non arriva, verifica l'indirizzo o contatta
				l'amministratore della CER.
			</AuthNote>
		);
	}

	return (
		<Stack gap="sm">
			{error && <AuthNotice>{error}</AuthNotice>}
			<AuthInlineEmailForm
				email={email}
				onEmailChange={setEmail}
				onSubmit={handleReset}
				loading={loading}
				hint={hint}
			/>
		</Stack>
	);
}
